/**
 * Bring a form's actions back into view when the keyboard takes the room they
 * were standing in.
 *
 * ── THE CASE THIS EXISTS FOR ───────────────────────────────────────────────
 * The two sign-in screens (`features/auth/auth-frame.tsx`) put the primary
 * button at `mt-auto`, on the reasoning that
 * `interactive-widget=resizes-content` shrinks the viewport and the button
 * rides up with it. It does — until the column is TALLER than what the
 * keyboard leaves, and then the column stops shrinking and the button goes
 * below the fold instead. `.app-scroll` scrolls, so it is reachable; nothing
 * says it is there. On the first screen of the app, on a short viewport, the
 * primary action is simply invisible (`T122`, 2026-09-21).
 *
 * Measured with the keyboard up, on the real IME inset of each target
 * (T126, 2026-09-22): the owner's handset leaves 691 px against a 432 px
 * column and clips nothing; the container leaves 383 and clips the button by
 * 25 px. Short viewports are real — a small phone, split-screen, a keyboard
 * with an extra row — so the screen must survive one.
 *
 * ── WHY `block: "nearest"` AND NOT `"end"` ─────────────────────────────────
 * `nearest` scrolls the least that brings the element fully into view, which
 * is NOTHING when it is already there. That is the whole property the fix was
 * chosen for: with the keyboard closed, on every viewport where the column
 * fits, this hook changes not one pixel of a screen that `docs/DESIGN.md` and
 * every capture so far have been made against.
 *
 * ── WHY IT WAITS ───────────────────────────────────────────────────────────
 * The viewport resizes on the KEYBOARD's animation, not on the focus, so a
 * scroll in the focus's own frame scrolls the old layout and lands short. The
 * hook therefore waits for the first `visualViewport` resize after the focus
 * and falls back to a timer, because a viewport that never resizes (a desktop
 * browser, or a WebView that pans instead of resizing) must not leave the
 * scroll unmade.
 *
 * ── IT HAS TO TELL T48 ─────────────────────────────────────────────────────
 * The element it scrolls is the same one `useToastClearance` holds, and that
 * measures a position. A scroll moves a position and fires neither of the two
 * signals it listens for, so the reveal announces itself — see `reveal()`.
 *
 * ── WHY THE LISTENER SITS ON THE DOCUMENT ──────────────────────────────────
 * Same reason as `dismiss-keyboard.ts`: an element read once when the effect
 * runs may not exist yet, and reading `ref.current` at EVENT time makes a late
 * mount irrelevant. It also keeps `.current` out of a `useCallback`, which the
 * React Compiler cannot lower (`T-168`).
 */
import { type RefObject, useEffect } from "react";

/** How long to wait for a viewport that is never going to resize. */
const SETTLE_FALLBACK_MS = 400;

/**
 * The `<input type>`s that raise a software keyboard — the same set
 * `dismiss-keyboard.ts` uses, and for the same reason: a checkbox, a range or
 * a file picker takes no room away.
 */
const TEXT_ENTRY_TYPES = new Set([
  "email",
  "number",
  "password",
  "search",
  "tel",
  "text",
  "url",
]);

function isTextEntry(element: Element | null): boolean {
  if (element instanceof HTMLTextAreaElement) {
    return true;
  }
  return (
    element instanceof HTMLInputElement && TEXT_ENTRY_TYPES.has(element.type)
  );
}

/**
 * When a text field inside the same form as `ref` takes focus, scroll `ref`
 * back into view once the viewport has settled.
 *
 * Scoped to the form on purpose: the hook's listener is on the document, and
 * a field in a toast, a sheet or anything else stacked over the screen must
 * not drag this form's actions around.
 */
export function useRevealOnKeyboard<T extends HTMLElement>(
  ref: RefObject<T | null>
): void {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let waiting: (() => void) | null = null;

    const done = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      if (waiting !== null) {
        visualViewport?.removeEventListener("resize", waiting);
        waiting = null;
      }
    };

    const reveal = () => {
      done();
      // Read at event time, never once when the effect ran.
      const actions = ref.current;
      if (actions === null) {
        return;
      }
      const before = actions.getBoundingClientRect().top;
      // `"instant"`, not `"smooth"`: the scroll has to be FINISHED before the
      // line below, and `scrollIntoView` has no completion callback. It also
      // lands under a keyboard that is still sliding in, which is what a
      // browser's own focus scroll does.
      actions.scrollIntoView({ behavior: "instant", block: "nearest" });
      if (actions.getBoundingClientRect().top === before) {
        // Nothing moved — the button was already in view. This is the case on
        // every viewport that fits, and it must stay completely inert.
        return;
      }
      // T48's `useToastClearance` shares this very ref and measures
      // `innerHeight - top`, re-measuring on `resize` and on a ResizeObserver.
      // A SCROLL changes `top` and fires neither, so without this the toast
      // would be placed against the position the actions used to have and
      // could sit back over the button T48 lifted it off. One event, only
      // when something actually moved.
      window.dispatchEvent(new Event("resize"));
    };

    const focused = (event: FocusEvent) => {
      const actions = ref.current;
      const form = actions?.closest("form") ?? null;
      if (
        form === null ||
        !(event.target instanceof Node) ||
        !form.contains(event.target) ||
        !isTextEntry(event.target as Element)
      ) {
        return;
      }
      done();
      waiting = reveal;
      visualViewport?.addEventListener("resize", waiting, { once: true });
      timer = setTimeout(reveal, SETTLE_FALLBACK_MS);
    };

    document.addEventListener("focusin", focused);
    return () => {
      done();
      document.removeEventListener("focusin", focused);
    };
  }, [ref]);
}
